import { useMemo, useState } from "react";
import { Filter } from "lucide-react";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon, Tick01Icon } from "@hugeicons/core-free-icons";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import type { ModelCatalogEntry } from "@/lib/types/model-catalog";
import type { ModelCategory } from "@/lib/types/module";
import { cn } from "@/lib/utils";

interface ModelComboboxProps {
  value: string | null;
  onSelect: (modelId: string, model?: ModelCatalogEntry) => void;
  categories?: ModelCategory[];
  includeBestOption?: boolean;
  bestValue?: string;
  bestLabel?: string;
  bestDescription?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  variant?: "input" | "pill";
  showProviderFilter?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
}

const MODEL_DESCRIPTIONS: Record<string, string> = {
  "claude-sonnet-4-6": "Anthropic balanced model.",
  "claude-sonnet-4-5": "Anthropic balanced model.",
  "claude-opus-4-6": "Anthropic most capable model.",
  "claude-haiku-4-5": "Anthropic fast model.",

  "gpt-5": "OpenAI flagship model.",
  "gpt-5-mini": "OpenAI fast lower-cost model.",
  "gpt-5-nano": "OpenAI lightest GPT-5 model.",
  "gpt-5.2": "OpenAI latest model.",
  o3: "OpenAI reasoning model.",
  "o4-mini": "OpenAI compact reasoning model.",
  "o3-mini": "OpenAI small reasoning model.",

  "gemini-3-flash-preview": "Google fast model.",
  "gemini-3-pro-preview": "Google most advanced model.",
  "gemini-2.5-pro": "Google high-capability model.",
  "gemini-2.0-flash": "Google low-latency model.",

  "gemini-2.5-flash-image": "Google fast image model.",
  "gemini-3-pro-image-preview": "Google advanced image model.",
  "imagen-3.0-generate-002": "Google high-fidelity image model.",
  "dall-e-3": "OpenAI detailed image model.",

  "flux-1.1-pro": "BFL quality image model.",
  "flux-1.1-pro-ultra": "BFL premium image model.",
  "flux-dev": "BFL development image model.",
  "flux-schnell": "BFL fast image model.",
  "sd3.5-large": "Stability large image model.",
  "sd3.5-medium": "Stability medium image model.",
  "ideogram-v3": "Ideogram text-strong image model.",

  "sora-2": "OpenAI video model.",
  "sora-2-pro": "OpenAI advanced video model.",
  "gen-4-turbo": "Runway fast video model.",
  "gen-4": "Runway flagship video model.",
  "veo-3.1": "Google advanced video model.",
  "veo-3.1-fast": "Google fast video model.",
  "veo-3": "Google quality video model.",
  "seedance-2": "ByteDance video model.",
  "pika-2.0": "Pika fast video model.",

  "tts-1": "OpenAI real-time TTS.",
  "tts-1-hd": "OpenAI high-quality TTS.",
  eleven_multilingual_v2: "ElevenLabs multilingual voice model.",

  "suno-v4": "Suno music model.",
  "suno-v5": "Suno latest music model.",
  "udio-130": "Udio music model.",
};

function fallbackDescription(model: ModelCatalogEntry): string {
  const category = model.categories[0] ?? "text";
  const categoryLabel: Record<ModelCategory, string> = {
    text: "text generation",
    image: "image generation",
    video: "video generation",
    audio: "audio generation",
    music: "music generation",
  };

  return `${model.provider_label}'s ${categoryLabel[category]} model.`;
}

function getDescription(model: ModelCatalogEntry): string {
  return MODEL_DESCRIPTIONS[model.id] ?? fallbackDescription(model);
}

function buildSearchIndex(model: ModelCatalogEntry, description: string): string {
  return [
    model.display_name,
    model.id,
    model.provider,
    model.provider_label,
    description,
  ]
    .join(" ")
    .toLowerCase();
}

export function ModelCombobox({
  value,
  onSelect,
  categories,
  includeBestOption = false,
  bestValue = "best",
  bestLabel = "Best",
  bestDescription = "Selects your best available model",
  placeholder = "Select model...",
  searchPlaceholder = "Search models...",
  variant = "input",
  showProviderFilter = true,
  triggerClassName,
  contentClassName,
  disabled = false,
}: ModelComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");

  const models = useModelCatalogStore((s) => s.models);
  const status = useModelCatalogStore((s) => s.status);

  const entries = useMemo(
    () =>
      models
        .filter((model) => {
          if (!categories || categories.length === 0) return true;
          return model.categories.some((category) => categories.includes(category));
        })
        .map((model) => {
          const description = getDescription(model);
          return {
            model,
            description,
            searchIndex: buildSearchIndex(model, description),
          };
        }),
    [categories, models],
  );

  const providerOptions = useMemo(() => {
    const labels = Array.from(new Set(entries.map((entry) => entry.model.provider_label)));
    return ["all", ...labels.sort((a, b) => a.localeCompare(b))];
  }, [entries]);

  const effectiveProviderFilter = providerOptions.includes(providerFilter)
    ? providerFilter
    : "all";

  const results = useMemo(() => {
    const tokens = search
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    return entries
      .filter((entry) => {
        if (
          effectiveProviderFilter !== "all" &&
          entry.model.provider_label !== effectiveProviderFilter
        ) {
          return false;
        }

        if (tokens.length === 0) return true;
        return tokens.every((token) => entry.searchIndex.includes(token));
      })
      .sort((a, b) => {
        if (a.model.provider_label !== b.model.provider_label) {
          return a.model.provider_label.localeCompare(b.model.provider_label);
        }
        return a.model.display_name.localeCompare(b.model.display_name);
      });
  }, [effectiveProviderFilter, entries, search]);

  const groupedResults = useMemo(() => {
    const grouped: Record<
      string,
      Array<{
        model: ModelCatalogEntry;
        description: string;
      }>
    > = {};

    for (const entry of results) {
      const key = entry.model.provider_label;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    }

    return Object.entries(grouped);
  }, [results]);

  const selected = useMemo(
    () => entries.find((entry) => entry.model.id === value),
    [entries, value],
  );

  const selectedLabel =
    value === bestValue
      ? bestLabel
      : (selected?.model.display_name ?? value ?? placeholder);

  const hasResults = groupedResults.length > 0;

  const triggerBaseClass =
    variant === "pill"
      ? "text-muted-foreground hover:text-foreground hover:bg-accent flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors cursor-pointer"
      : "flex h-10 w-full items-center justify-between px-3 text-sm text-left bg-black/5 dark:bg-white/[0.08] rounded-xl hover:bg-black/[0.08] dark:hover:bg-white/10 outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(triggerBaseClass, triggerClassName)}
        >
          <span
            className={cn(
              "truncate",
              value ? "text-foreground" : "text-muted-foreground/50",
            )}
          >
            {selectedLabel}
          </span>
          <HugeiconsIcon icon={ArrowDown01Icon} className="size-3 shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className={cn(
          "p-0 gap-0 flex flex-col max-h-[min(460px,78vh)] overflow-hidden",
          variant === "pill"
            ? "w-[360px] max-w-[min(92vw,360px)]"
            : "w-[var(--radix-popover-trigger-width)] min-w-[240px]",
          contentClassName,
        )}
      >
        <div className="p-2 border-b border-border/30 shrink-0 flex items-center gap-2">
          <Input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 rounded-lg border-none bg-black/5 dark:bg-white/[0.08] text-sm placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-ring/50"
          />

          {showProviderFilter && providerOptions.length > 2 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "h-8 w-8 shrink-0 rounded-lg border border-border/50 bg-black/5 dark:bg-white/[0.08] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors flex items-center justify-center",
                    effectiveProviderFilter !== "all" && "text-foreground",
                  )}
                  title="Filter by provider"
                >
                  <Filter className="size-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuRadioGroup
                  value={effectiveProviderFilter}
                  onValueChange={setProviderFilter}
                >
                  <DropdownMenuRadioItem value="all">
                    All providers
                  </DropdownMenuRadioItem>
                  {providerOptions
                    .filter((provider) => provider !== "all")
                    .map((provider) => (
                      <DropdownMenuRadioItem key={provider} value={provider}>
                        {provider}
                      </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {includeBestOption && (
              <>
                <button
                  type="button"
                  className={cn(
                    "w-full px-3 py-2.5 text-left transition-colors hover:bg-accent",
                    value === bestValue && "bg-accent/50",
                  )}
                  onClick={() => {
                    onSelect(bestValue);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{bestLabel}</span>
                    {value === bestValue && (
                      <HugeiconsIcon
                        icon={Tick01Icon}
                        className="size-4 shrink-0 text-[oklch(0.55_0.25_285)]"
                      />
                    )}
                  </div>
                  {bestDescription && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {bestDescription}
                    </p>
                  )}
                </button>
                <div className="mx-3 h-px bg-border/40" />
              </>
            )}

            {status === "loading" && models.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Loading models...
              </div>
            ) : status === "error" && models.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Unable to load model catalog
              </div>
            ) : hasResults ? (
              <div className="py-1">
                {groupedResults.map(([provider, providerEntries]) => (
                  <div key={provider}>
                    <div className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                      {provider}
                    </div>
                    {providerEntries.map((entry) => (
                      <button
                        key={entry.model.id}
                        type="button"
                        className={cn(
                          "w-full px-3 py-2 text-left transition-colors hover:bg-accent",
                          value === entry.model.id && "bg-accent/50",
                        )}
                        onClick={() => {
                          onSelect(entry.model.id, entry.model);
                          setOpen(false);
                          setSearch("");
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">
                              {entry.model.display_name}
                            </div>
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">
                              {entry.description}
                            </p>
                          </div>
                          {value === entry.model.id && (
                            <HugeiconsIcon
                              icon={Tick01Icon}
                              className="size-4 shrink-0 text-[oklch(0.55_0.25_285)]"
                            />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No models found
              </div>
            )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
